import { Document, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, Packer, AlignmentType, WidthType, BorderStyle, ShadingType } from "docx";

function parseInlineFormatting(text) {
  const runs = [];
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|([^*`]+)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m[1]) runs.push(new TextRun({ text: m[1], bold: true, font: "Source Sans 3", size: 22 }));
    else if (m[2]) runs.push(new TextRun({ text: m[2], italics: true, font: "Source Sans 3", size: 22 }));
    else if (m[3]) runs.push(new TextRun({ text: m[3], font: "Consolas", size: 20, shading: { type: ShadingType.SOLID, color: "F0F0F0" } }));
    else if (m[4]) runs.push(new TextRun({ text: m[4], font: "Source Sans 3", size: 22 }));
  }
  return runs.length ? runs : [new TextRun({ text, font: "Source Sans 3", size: 22 })];
}

function parseTable(tableBlock) {
  const lines = tableBlock.trim().split("\n").filter(l => !l.match(/^\|[-| :]+\|$/));
  if (lines.length < 1) return null;
  const parseRow = r => r.split("|").slice(1, -1).map(c => c.trim());
  const headers = parseRow(lines[0]);
  const dataRows = lines.slice(1).map(parseRow);

  const headerRow = new TableRow({
    tableHeader: true,
    children: headers.map(h => new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, font: "Source Sans 3", size: 20 })], spacing: { before: 40, after: 40 } })],
      shading: { type: ShadingType.SOLID, color: "F5F5F5" },
    })),
  });

  const rows = dataRows.map(cells => new TableRow({
    children: cells.map(c => new TableCell({
      children: [new Paragraph({ children: parseInlineFormatting(c), spacing: { before: 30, after: 30 } })],
    })),
  }));

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...rows],
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
      left: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
      right: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "DDDDDD" },
    },
  });
}

export async function markdownToDocx(md, meta) {
  const children = [];

  children.push(new Paragraph({ spacing: { after: 100 }, children: [new TextRun({ text: "SAP CPI IFLOW DOKUMENTATION", font: "Source Sans 3", size: 18, color: "888888" })] }));
  children.push(new Paragraph({ heading: HeadingLevel.TITLE, spacing: { after: 60 }, children: [new TextRun({ text: meta.flowName || "iFlow", font: "Source Sans 3", size: 44, bold: true })] }));
  children.push(new Paragraph({ spacing: { after: 400 }, children: [new TextRun({ text: `${meta.styleLabel} - ${meta.date}`, font: "Source Sans 3", size: 20, color: "666666" })] }));

  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (line.match(/^```/)) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].match(/^```/)) { codeLines.push(lines[i]); i++; }
      i++;
      children.push(new Paragraph({
        spacing: { before: 80, after: 80 },
        shading: { type: ShadingType.SOLID, color: "F5F5F5" },
        children: [new TextRun({ text: codeLines.join("\n"), font: "Consolas", size: 18 })],
      }));
      continue;
    }

    const tableStart = line.match(/^\|.+\|$/);
    if (tableStart) {
      let tableBlock = "";
      while (i < lines.length && lines[i].match(/^\|/)) { tableBlock += lines[i] + "\n"; i++; }
      const table = parseTable(tableBlock);
      if (table) children.push(table);
      continue;
    }

    const diagMarker = line.match(/^\s*\[\[\s*DIAGRAMM\s*:[^\]|]+(?:\|([^\]]*))?\]\]\s*$/i);
    if (diagMarker) {
      const cap = (diagMarker[1] || "").trim();
      if (cap) children.push(new Paragraph({ spacing: { before: 40, after: 80 }, children: [new TextRun({ text: `Abbildung: ${cap}`, italics: true, font: "Source Sans 3", size: 20, color: "888888" })] }));
      i++; continue;
    }

    const h1 = line.match(/^#\s+(.+)$/);
    if (h1) { children.push(new Paragraph({ heading: HeadingLevel.HEADING_1, spacing: { before: 240, after: 120 }, children: parseInlineFormatting(h1[1]) })); i++; continue; }

    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) { children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 100 }, children: parseInlineFormatting(h2[1]) })); i++; continue; }

    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) { children.push(new Paragraph({ heading: HeadingLevel.HEADING_3, spacing: { before: 160, after: 80 }, children: parseInlineFormatting(h3[1]) })); i++; continue; }

    const h4 = line.match(/^####\s+(.+)$/);
    if (h4) { children.push(new Paragraph({ heading: HeadingLevel.HEADING_4, spacing: { before: 120, after: 60 }, children: parseInlineFormatting(h4[1]) })); i++; continue; }

    const li = line.match(/^[-*]\s+(.+)$/);
    if (li) { children.push(new Paragraph({ bullet: { level: 0 }, spacing: { before: 20, after: 20 }, children: parseInlineFormatting(li[1]) })); i++; continue; }

    const oli = line.match(/^\d+\.\s+(.+)$/);
    if (oli) { children.push(new Paragraph({ numbering: { reference: "default-numbering", level: 0 }, spacing: { before: 20, after: 20 }, children: parseInlineFormatting(oli[1]) })); i++; continue; }

    if (line.trim() === "") { children.push(new Paragraph({ spacing: { before: 60, after: 60 } })); i++; continue; }

    children.push(new Paragraph({ spacing: { before: 40, after: 40 }, children: parseInlineFormatting(line) }));
    i++;
  }

  const doc = new Document({
    numbering: { config: [{ reference: "default-numbering", levels: [{ level: 0, format: "decimal", text: "%1.", alignment: AlignmentType.START }] }] },
    sections: [{ children }],
  });

  return Packer.toBlob(doc);
}
